import React, { useState, useEffect } from 'react';
import { User, LogOut, Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import UserProfile from './UserProfile';

const Header = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setShowMobileMenu(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowMobileMenu(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Close auth modal when user logs in
  useEffect(() => {
    if (currentUser && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [currentUser, showAuthModal]);

  // Listen for custom events to open auth modal
  useEffect(() => {
    const handleOpenAuthModal = (event) => {
      const { mode } = event.detail;
      setAuthMode(mode);
      setShowAuthModal(true);
    };

    window.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => {
      window.removeEventListener('openAuthModal', handleOpenAuthModal);
    };
  }, []);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200" style={{ fontFamily: 'Poppins,Roboto,Helvetica,Arial,sans-serif' }}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
           <div>
              <button 
                onClick={() => navigate('/')}
                className="text-left hover:opacity-80 transition-opacity duration-200 flex items-center group cursor-pointer"
              >
                {/* Logo Mark - Three circles in triangular pattern */}
                <div className="flex items-center mr-3">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Top-left circle - vibrant pink/magenta */}
                    <circle cx="8" cy="8" r="6" fill="#FF0080" className="group-hover:scale-110 transition-transform" />
                    {/* Bottom-left circle - medium purple/blue */}
                    <circle cx="8" cy="24" r="6" fill="#8B5CF6" className="group-hover:scale-110 transition-transform" />
                    {/* Right circle - bright blue */}
                    <circle cx="24" cy="16" r="6" fill="#00BFFF" className="group-hover:scale-110 transition-transform" />
                  </svg>
                </div>
                
                {/* Wordmark */}
                <div className="flex flex-col">
                  <h1 className="font-bold text-gray-900 leading-none" style={{ fontSize: '24px', letterSpacing: '-0.5px' }}>
                    ArcRefinery
                  </h1>
                  <p className="mt-0.5 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 bg-clip-text text-transparent" style={{ fontSize: '12px', fontWeight: 300 }}>
                    Storytelling meets AI
                  </p>
                </div>
              </button>
            </div>
          
          <div className="flex items-center space-x-6">
            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="relative px-4 py-2 cursor-pointer rounded-lg transition-all duration-300 hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-500 hover:shadow-lg hover:scale-105 group"
                style={{ color: '#535768', fontSize: '14px', fontWeight: 300 }}
              >
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">Home</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button 
                onClick={() => navigate('/frameworks')}
                className="relative px-4 py-2 cursor-pointer rounded-lg transition-all duration-300 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:shadow-lg hover:scale-105 group"
                style={{ color: '#535768', fontSize: '14px', fontWeight: 300 }}
              >
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">Frameworks</span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button 
                onClick={() => navigate('/why-arcrefinery')}
                className="relative px-4 py-2 cursor-pointer rounded-lg transition-all duration-300 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-500 hover:shadow-lg hover:scale-105 group"
                style={{ color: '#535768', fontSize: '14px', fontWeight: 300 }}
              >
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">Why ArcRefinery</span>
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button 
                onClick={() => navigate('/platforms')}
                className="relative px-4 py-2 cursor-pointer rounded-lg transition-all duration-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:shadow-lg hover:scale-105 group"
                style={{ color: '#535768', fontSize: '14px', fontWeight: 300 }}
              >
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">Platforms</span>
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </nav>

            {/* Authentication Section */}
            <div className="flex items-center space-x-4">
              {currentUser ? (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {currentUser.displayName?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
                    </div>
                    <span className="hidden md:block text-sm font-medium">
                      {currentUser.displayName || 'User'}
                    </span>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleAuthClick('login')}
                    className="relative group transition-all duration-200 hover:scale-105"
                    style={{ color: '#535768', fontSize: '14px', fontWeight: 300 }}
                  >
                    <span className="relative group-hover:text-purple-600 transition-colors duration-200">Sign In</span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600 group-hover:w-full transition-all duration-300"></span>
                  </button>
                  <button
                    onClick={() => handleAuthClick('signup')}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-md hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
                    style={{ fontSize: '14px', fontWeight: 300 }}
                  >
                    Sign Up
                  </button>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden text-gray-700 hover:text-gray-900"
              >
                {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="pt-4 space-y-2">
              <button 
                onClick={() => { navigate('/'); setShowMobileMenu(false); }}
                className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                Home
              </button>
              <button 
                onClick={() => { navigate('/frameworks'); setShowMobileMenu(false); }}
                className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                Frameworks
              </button>
              <button 
                onClick={() => { navigate('/why-arcrefinery'); setShowMobileMenu(false); }}
                className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                Why ArcRefinery
              </button>
              <button 
                onClick={() => { navigate('/platforms'); setShowMobileMenu(false); }}
                className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                Platforms
              </button>
              
              {currentUser ? (
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="flex items-center space-x-2 w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  >
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 w-full text-left px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <div className="pt-2 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => handleAuthClick('login')}
                    className="block w-full text-left px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => handleAuthClick('signup')}
                    className="block w-full text-left px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-md hover:from-purple-700 hover:to-pink-700"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Auth Modal - Only show if user is not logged in */}
      {!currentUser && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
        />
      )}

      {/* User Profile Modal */}
      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />
    </header>
  );
};

export default Header;
